<script setup>
import ProductImageConfigurationForm from '@frontJs/components/forms/projectManagement/ai/ProductImageConfigurationForm.vue';
import ProductImageAttachmentTable from '@frontJs/components/tables/projectManagement/ai/ProductImageAttachmentTable.vue';
import ProductImageTable from '@frontJs/components/tables/projectManagement/ai/ProductImageTable.vue';
import TenantReportProductImageTable from '@frontJs/components/tables/projectManagement/ai/tenantReports/ProductImageTable.vue';
import ProjectManagementLayout from '@frontJs/layouts/ProjectManagementLayout.vue';
import { Head } from '@inertiajs/vue3';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import PageTitle from '@js/components/titles/PageTitle.vue';
import { h, onMounted, ref } from 'vue';

const { tenant, configuration } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    configuration: {
        type: Object,
        required: false,
    },
});

const loading = ref(true);

const data = ref({
    toDo: 0,
    inProgress: 0,
    toBeApproved: 0,
    approved: 0,
});

function updateData() {
    loading.value = true;

    Promise.all([
        axios.post(
            route('front.projectManagement.ai.postProductImageCount', {
                tenant: tenant.id,
            }),
            {
                status: 'toDo',
            },
        ),
        axios.post(
            route('front.projectManagement.ai.postProductImageCount', {
                tenant: tenant.id,
            }),
            {
                status: 'inProgress',
            },
        ),
        axios.post(
            route('front.projectManagement.ai.postProductImageCount', {
                tenant: tenant.id,
            }),
            {
                status: 'toBeApproved',
            },
        ),
        axios.post(
            route('front.projectManagement.ai.postProductImageCount', {
                tenant: tenant.id,
            }),
            {
                status: 'approved',
            },
        ),
    ])
        .then((responses) => {
            data.value.toDo = responses[0].data.count;
            data.value.inProgress = responses[1].data.count;
            data.value.toBeApproved = responses[2].data.count;
            data.value.approved = responses[3].data.count;
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

onMounted(() => {
    if (tenant) {
        updateData();
    }
});

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
    <Head>
        <title>Images produits - IA - Gestion de projet - Datakepler</title>
    </Head>

    <PageTitle :tenant-name="tenant ? tenant.name : undefined">
        Gestion de projet - IA - Images produits
    </PageTitle>

    <TabLayout
        v-if="tenant"
        :tabs="{
            images: {
                title: 'Images',
                items: {
                    toDo: {
                        title: 'À faire',
                        total: data.toDo,
                        content: h(ProductImageTable, {
                            tenant: tenant,
                            status: 'toDo',
                            callback: () => updateData(),
                        }),
                    },
                    inProgress: {
                        title: 'En cours',
                        total: data.inProgress,
                        content: h(ProductImageTable, {
                            tenant: tenant,
                            status: 'inProgress',
                            callback: () => updateData(),
                        }),
                    },
                    toBeApproved: {
                        title: 'À valider',
                        total: data.toBeApproved,
                        content: h(ProductImageTable, {
                            tenant: tenant,
                            status: 'toBeApproved',
                            callback: () => updateData(),
                        }),
                    },
                    approved: {
                        title: 'Validées',
                        total: data.approved,
                        content: h(ProductImageTable, {
                            tenant: tenant,
                            status: 'approved',
                            callback: () => updateData(),
                        }),
                    },
                },
            },
            templates: {
                title: 'Pièces jointes',
                content: h(ProductImageAttachmentTable, {
                    tenant: tenant,
                }),
            },
            configuration: {
                title: 'Configuration',
                content: h(ProductImageConfigurationForm, {
                    tenant: tenant,
                    configuration,
                }),
            },
        }"
        :loading="loading"
    />

    <TenantReportProductImageTable v-else />
</template>
